<template>
  <div class="card-cont-wrapper fade-in-out">
    <div class="dashboardSection">
      <div class="row alignCentreRow spaceBetween">
        <h3 class="nomargintopbottom">API keys</h3>
        <UnitButton
          v-if="authorityReadsEnabled"
          size="small"
          :disabled="loading || !hasContext"
          @click="load"
        >
          {{ loading ? "Refreshing…" : "Refresh" }}
        </UnitButton>
      </div>

      <p class="smallText grayText">
        Server-to-server metering keys are scoped, expiring credentials. Their
        secret is shown only once.
      </p>

      <div
        v-if="accessDenied"
        class="smallText grayText"
        data-api-key-access-denied
      >
        You do not have permission to manage API keys for this organisation.
      </div>

      <div
        v-else-if="!authorityReadsEnabled"
        class="smallText grayText"
        data-api-key-read-gate-disabled
      >
        API key management is not enabled.
      </div>

      <template v-else>
        <div
          v-if="error"
          class="smallText feedback-danger-text"
          style="margin-top: var(--space-3)"
          role="alert"
        >
          {{ error.message || "API key management is unavailable." }}
        </div>

        <div
          v-if="authorityCommandsEnabled && !accessDenied"
          class="row gap"
          style="margin-top: var(--space-4)"
        >
          <UnitButton
            size="small"
            :disabled="loading || !hasContext"
            data-api-key-new
            @click="openCreateModal"
          >
            New key
          </UnitButton>
        </div>
        <p
          v-else-if="!accessDenied"
          class="smallText grayText"
          data-api-key-command-gate-disabled
        >
          Key creation, rotation and revocation are not enabled.
        </p>

        <Modal
          :visible="showSecretModal"
          :title="modalTitle"
          :description="modalDescription"
          placement="sheet"
          :close-on-backdrop="!loading"
          :close-on-escape="!loading"
          :show-close-button="!loading"
          initial-focus-selector="#apiKeyName"
          @close="closeSecretModal"
        >
          <div class="column fullWidth">
            <template v-if="modalStep === 'enterName'">
              <label class="nomargintopbottom" for="apiKeyName">
                Key name
              </label>
              <input
                id="apiKeyName"
                v-model="modalName"
                class="fullWidth alignCentre"
                type="text"
                maxlength="80"
                autocomplete="off"
                placeholder="Key name (e.g., reporting service)"
              />
              <label class="nomargintopbottom" for="apiKeyPurpose">
                Purpose
              </label>
              <select
                id="apiKeyPurpose"
                v-model="modalPurpose"
                class="fullWidth alignCentre"
              >
                <option value="federation-provisioning">
                  Orus federation provisioning
                </option>
                <option value="metering">Server metering</option>
              </select>
              <p class="smallText grayText">
                {{ selectedKeyPurpose.description }}
              </p>
            </template>
            <template v-else>
              <p
                class="alignCentre tinymargintopbottom feedback-danger-text smallText"
              >
                Never share this key. Treat it like a password.
              </p>
              <div class="warningRow row alignCentreRow gap">
                <svg
                  class="icon compactImg feedback-danger-text"
                  aria-hidden="true"
                >
                  <use
                    href="@/assets/icons/iconset.svg#error_warning_compact"
                  ></use>
                </svg>
                <span class="feedback-danger-text smallText">
                  Copy this secret now. It will not be shown again after this
                  dialog closes.
                </span>
              </div>
              <div style="margin-top: var(--space-4)">
                <MetaMaskAccountNumber
                  :address="issuedToken"
                  :truncate="false"
                  label-text="API key:"
                  copy-confirmed-message="API key copied"
                />
                <div
                  v-if="issuedFingerprint"
                  class="smallText grayText"
                  style="margin-top: var(--space-3)"
                >
                  Fingerprint {{ issuedFingerprint }}
                </div>
              </div>
            </template>
          </div>

          <template #footer>
            <template v-if="modalStep === 'enterName'">
              <UnitButton
                variant="secondary"
                :disabled="loading"
                data-api-key-cancel
                @click="closeSecretModal"
              >
                Cancel
              </UnitButton>
              <UnitButton
                :disabled="!modalName.trim()"
                :loading="loading"
                loading-label="Generating key"
                data-api-key-generate
                @click="generateInModal"
              >
                Generate key
              </UnitButton>
            </template>
            <template v-else>
              <UnitButton
                variant="secondary"
                data-api-key-copy
                @click="copySecret"
              >
                Copy
              </UnitButton>
              <UnitButton data-api-key-saved @click="closeSecretModal">
                I saved it
              </UnitButton>
            </template>
          </template>
        </Modal>

        <div
          v-if="!accessDenied && keys.length"
          class="usageTableWrapper"
          style="margin-top: var(--space-4)"
        >
          <table class="usageTable smallText">
            <thead>
              <tr>
                <th>Name</th>
                <th>Fingerprint</th>
                <th>Status</th>
                <th>Expires</th>
                <th v-if="authorityCommandsEnabled">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="key in keys" :key="key.keyId">
                <td>{{ key.name }}</td>
                <td class="mono">{{ key.fingerprint }}</td>
                <td>{{ key.status === "active" ? "Active" : "Revoked" }}</td>
                <td>{{ toDate(key.expiresAt) }}</td>
                <td v-if="authorityCommandsEnabled" class="api-key-actions">
                  <template v-if="key.status === 'active'">
                    <InlineActionFeedback
                      class="api-key-inline-action api-key-inline-action--neutral"
                      :action="() => rotateKey(key)"
                      :aria-label="`Rotate API key ${keyLabel(key)}`"
                      confirmation-message="Rotate?"
                      :confirm-aria-label="
                        `Confirm rotation of API key ${keyLabel(key)}`
                      "
                      error-message="Rotation failed"
                      :disabled="isInlineActionDisabled(key, 'rotate')"
                      title="Rotate key"
                      data-api-key-rotate
                      @state-change="
                        (state) =>
                          handleInlineActionState(key, 'rotate', state)
                      "
                    >
                      Rotate
                    </InlineActionFeedback>
                    <InlineActionFeedback
                      class="api-key-inline-action"
                      :action="() => revokeKey(key)"
                      :aria-label="`Revoke API key ${keyLabel(key)}`"
                      confirmation-message="Revoke?"
                      :confirm-aria-label="
                        `Confirm revocation of API key ${keyLabel(key)}`
                      "
                      error-message="Revocation failed"
                      tone="danger"
                      :disabled="isInlineActionDisabled(key, 'revoke')"
                      title="Revoke key"
                      data-api-key-revoke
                      @state-change="
                        (state) =>
                          handleInlineActionState(key, 'revoke', state)
                      "
                    >
                      Revoke
                    </InlineActionFeedback>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else-if="!accessDenied && !loading"
          class="smallText grayText"
          style="margin-top: var(--space-4)"
          data-api-key-empty
        >
          No keys yet.
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";
import { toast } from "vue3-toastify";

import Modal from "@/components/GenericComponents/Modal.vue";
import InlineActionFeedback from "@/components/GenericComponents/InlineActionFeedback.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import MetaMaskAccountNumber from "@/components/WalletComponents/MetaMaskAccountNumber.vue";
import {
  apiKeyAuthorityService,
  createApiKeyAuthorityOperationId,
} from "@/services/apiKeyAuthorityService";
import {
  isApiKeyAuthorityCommandsFeatureEnabled,
  isApiKeyAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { copyTextToClipboard } from "@/utils/clipboard";

const KEY_PURPOSES = Object.freeze({
  "federation-provisioning": Object.freeze({
    scopes: Object.freeze(["federated-organisation:provision"]),
    audiences: Object.freeze(["federation-provisioning"]),
    expiresInDays: 365,
    quota: Object.freeze({ windowSeconds: 60, maxRequests: 100 }),
    description:
      "Creates and updates Orus member organisations. Expires after one year and is limited to 100 requests per minute.",
  }),
  metering: Object.freeze({
    scopes: Object.freeze(["usage:write"]),
    audiences: Object.freeze(["metering"]),
    expiresInDays: 30,
    quota: Object.freeze({ windowSeconds: 60, maxRequests: 100 }),
    description:
      "Submits server metering usage. Expires after 30 days and is limited to 100 requests per minute.",
  }),
});

export default {
  name: "ApiKeysPanel",
  components: {
    InlineActionFeedback,
    Modal,
    MetaMaskAccountNumber,
    UnitButton,
  },
  props: {
    orgId: { type: String, required: true },
    actorUid: { type: String, required: true },
    authorityReadsEnabled: {
      type: Boolean,
      default: isApiKeyAuthorityReadsFeatureEnabled,
    },
    authorityCommandsEnabled: {
      type: Boolean,
      default: isApiKeyAuthorityCommandsFeatureEnabled,
    },
  },
  setup(props) {
    const keys = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const showSecretModal = ref(false);
    const modalStep = ref("enterName");
    const modalName = ref("");
    const modalPurpose = ref("federation-provisioning");
    const issuedToken = ref("");
    const issuedFingerprint = ref("");
    const issuanceKind = ref("created");
    const accessDenied = ref(false);
    const pendingOperationIds = new Map();
    let contextGeneration = 0;
    let loadGeneration = 0;

    const hasContext = computed(
      () => Boolean(props.actorUid && props.orgId),
    );
    const selectedKeyPurpose = computed(
      () => KEY_PURPOSES[modalPurpose.value] || KEY_PURPOSES.metering,
    );
    const modalTitle = computed(() => {
      if (modalStep.value === "enterName") return "Create API key";
      return issuanceKind.value === "rotated"
        ? "API key rotated"
        : "API key created";
    });
    const modalDescription = computed(() =>
      modalStep.value === "enterName"
        ? "Give this key a name so you can recognise it later."
        : "Copy and store this secret now — it will not be shown again.",
    );

    const normalize = (value) =>
      [...(value || [])].map((key) => ({ ...key })).sort((left, right) => {
        if (left.status !== right.status) {
          return left.status === "active" ? -1 : 1;
        }
        return right.createdAt - left.createdAt;
      });

    const captureContext = () =>
      Object.freeze({
        generation: contextGeneration,
        actorUid: props.actorUid,
        orgId: props.orgId,
      });

    const contextIsCurrent = (context) =>
      context.generation === contextGeneration &&
      context.actorUid === props.actorUid &&
      context.orgId === props.orgId;

    const operationIdFor = (parts) => {
      const key = JSON.stringify(parts);
      if (!pendingOperationIds.has(key)) {
        pendingOperationIds.set(key, createApiKeyAuthorityOperationId());
      }
      return Object.freeze({ key, operationId: pendingOperationIds.get(key) });
    };

    const clearIssuedSecret = () => {
      issuedToken.value = "";
      issuedFingerprint.value = "";
      issuanceKind.value = "created";
    };

    const resetModal = async () => {
      // Clear bearer material while the dialog is still mounted. Otherwise a
      // leave transition can retain the old token text until its animation
      // completes even though Vue state has already been reset.
      clearIssuedSecret();
      await nextTick();
      showSecretModal.value = false;
      modalStep.value = "enterName";
      modalName.value = "";
      modalPurpose.value = "federation-provisioning";
    };

    const load = async () => {
      if (!props.authorityReadsEnabled || !hasContext.value) return;
      const context = captureContext();
      const requestGeneration = ++loadGeneration;
      loading.value = true;
      try {
        const result = await apiKeyAuthorityService.listAuthorityApiKeys(
          context.orgId,
        );
        if (
          !contextIsCurrent(context) ||
          requestGeneration !== loadGeneration
        ) {
          return;
        }
        error.value = null;
        accessDenied.value = false;
        keys.value = normalize(result.keys);
      } catch (caught) {
        if (
          !contextIsCurrent(context) ||
          requestGeneration !== loadGeneration
        ) {
          return;
        }
        keys.value = [];
        accessDenied.value = caught?.code === "permission-denied";
        error.value = accessDenied.value ? null : caught;
      } finally {
        if (
          contextIsCurrent(context) &&
          requestGeneration === loadGeneration
        ) {
          loading.value = false;
        }
      }
    };

    const openCreateModal = () => {
      if (!props.authorityCommandsEnabled || !hasContext.value) return;
      error.value = null;
      modalStep.value = "enterName";
      modalName.value = "";
      modalPurpose.value = "federation-provisioning";
      clearIssuedSecret();
      showSecretModal.value = true;
    };

    const revealIssuedSecret = async (result, kind, context) => {
      if (!contextIsCurrent(context)) return false;
      // The command has completed. Release the busy guard before presenting
      // the one-time token so an immediate Save/Close action cannot be lost.
      loading.value = false;
      issuedToken.value = result.token;
      issuedFingerprint.value = result.key.fingerprint;
      issuanceKind.value = kind;
      modalStep.value = "showSecret";
      showSecretModal.value = true;
      await nextTick();
      document.querySelector("[data-api-key-copy]")?.focus();
      return true;
    };

    const generateInModal = async () => {
      const name = modalName.value.trim();
      if (!name || !props.authorityCommandsEnabled || !hasContext.value) {
        return;
      }
      const context = captureContext();
      const keyPurpose = selectedKeyPurpose.value;
      const operation = operationIdFor([
        "create",
        context.actorUid,
        context.orgId,
        name,
        keyPurpose.scopes,
        keyPurpose.audiences,
        keyPurpose.expiresInDays,
        keyPurpose.quota,
      ]);
      loading.value = true;
      try {
        error.value = null;
        const result = await apiKeyAuthorityService.createAuthorityApiKey({
          operationId: operation.operationId,
          expectedRevision: 0,
          orgId: context.orgId,
          name,
          scopes: [...keyPurpose.scopes],
          audiences: [...keyPurpose.audiences],
          expiresInDays: keyPurpose.expiresInDays,
          quota: { ...keyPurpose.quota },
        });
        if (await revealIssuedSecret(result, "created", context)) {
          pendingOperationIds.delete(operation.key);
        }
      } catch (caught) {
        if (contextIsCurrent(context)) error.value = caught;
      } finally {
        if (contextIsCurrent(context)) loading.value = false;
      }
    };

    const closeSecretModal = async () => {
      if (loading.value) return;
      const shouldRefresh = modalStep.value === "showSecret";
      await resetModal();
      if (shouldRefresh) void load();
    };

    const copySecret = async () => {
      if (!issuedToken.value) return;
      try {
        await copyTextToClipboard(issuedToken.value);
        toast.success("API key copied to clipboard");
      } catch (_error) {
        toast.error("Failed to copy");
      }
    };

    const keyLabel = (key) => key?.name || key?.fingerprint || "unnamed key";

    const handleInlineActionState = (key, action, state) => {
      if (state === "confirming") {
        key.__armedAction = action;
        return;
      }
      if (key.__armedAction === action) key.__armedAction = null;
    };

    const isInlineActionDisabled = (key, action) =>
      loading.value ||
      !props.authorityCommandsEnabled ||
      (Boolean(key.__armedAction) && key.__armedAction !== action) ||
      (Boolean(key.__pendingAction) && key.__pendingAction !== action);

    const rotateKey = async (key) => {
      if (!props.authorityCommandsEnabled) return;
      if (key.__armedAction === "rotate") key.__armedAction = null;
      key.__pendingAction = "rotate";
      const context = captureContext();
      const operation = operationIdFor([
        "rotate",
        context.actorUid,
        context.orgId,
        key.keyId,
        key.revision,
      ]);
      try {
        error.value = null;
        const result = await apiKeyAuthorityService.rotateAuthorityApiKey({
          operationId: operation.operationId,
          expectedRevision: key.revision,
          orgId: context.orgId,
          keyId: key.keyId,
        });
        if (await revealIssuedSecret(result, "rotated", context)) {
          pendingOperationIds.delete(operation.key);
        }
      } catch (caught) {
        if (contextIsCurrent(context)) error.value = caught;
        throw caught;
      } finally {
        if (key.__pendingAction === "rotate") key.__pendingAction = null;
      }
    };

    const revokeKey = async (key) => {
      if (!props.authorityCommandsEnabled) return;
      if (key.__armedAction === "revoke") key.__armedAction = null;
      key.__pendingAction = "revoke";
      const context = captureContext();
      const operation = operationIdFor([
        "revoke",
        context.actorUid,
        context.orgId,
        key.keyId,
        key.revision,
      ]);
      try {
        error.value = null;
        const result = await apiKeyAuthorityService.revokeAuthorityApiKey({
          operationId: operation.operationId,
          expectedRevision: key.revision,
          orgId: context.orgId,
          keyId: key.keyId,
        });
        pendingOperationIds.delete(operation.key);
        if (!contextIsCurrent(context)) return;
        keys.value = normalize(
          keys.value.map((candidate) =>
            candidate.keyId === result.key.keyId ? result.key : candidate,
          ),
        );
      } catch (caught) {
        if (contextIsCurrent(context)) error.value = caught;
        throw caught;
      } finally {
        if (key.__pendingAction === "revoke") key.__pendingAction = null;
      }
    };

    const toDate = (timestamp) => {
      const date = new Date(timestamp);
      return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
    };

    watch(
      () => [props.actorUid, props.orgId, props.authorityReadsEnabled],
      async () => {
        contextGeneration += 1;
        loadGeneration += 1;
        loading.value = false;
        error.value = null;
        accessDenied.value = false;
        keys.value = [];
        pendingOperationIds.clear();
        await resetModal();
        await load();
      },
      { immediate: true },
    );

    onBeforeUnmount(() => {
      contextGeneration += 1;
      loadGeneration += 1;
      clearIssuedSecret();
      pendingOperationIds.clear();
    });

    return {
      keys,
      loading,
      error,
      accessDenied,
      showSecretModal,
      modalStep,
      modalName,
      modalPurpose,
      selectedKeyPurpose,
      issuedToken,
      issuedFingerprint,
      hasContext,
      modalTitle,
      modalDescription,
      load,
      openCreateModal,
      generateInModal,
      closeSecretModal,
      copySecret,
      keyLabel,
      handleInlineActionState,
      isInlineActionDisabled,
      rotateKey,
      revokeKey,
      toDate,
    };
  },
};
</script>

<style scoped>
.warningRow .icon {
  color: var(--color-feedback-danger-foreground);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

.api-key-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-4);
}

.api-key-inline-action {
  box-sizing: border-box;
  flex: 0 0 auto;
  width: fit-content;
  height: calc(var(--control-height-compact) * 0.85);
  padding: 0 var(--space-5);
  border: 1px solid var(--color-feedback-danger-border);
  border-radius: var(--radius-pill);
  background: var(--color-feedback-danger-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  transition:
    background-color var(--motion-standard) var(--motion-easing-standard),
    border-color var(--motion-standard) var(--motion-easing-standard),
    color var(--motion-standard) var(--motion-easing-standard);
}

.api-key-inline-action--neutral {
  border-color: var(--color-border-subtle);
  background: var(--color-surface-muted);
}

.api-key-inline-action:not(:disabled):hover {
  background: var(--color-feedback-danger-surface-emphasis);
}

.api-key-inline-action--neutral:not(:disabled):hover {
  background: var(--color-surface-interactive-active);
}

.api-key-inline-action:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
